/**
 * pptx-context — reads the currently selected slide (its shapes + text) in
 * PowerPoint and exposes it as a HostContextProvider so the assistant can act on
 * "this slide" without the user describing it. Mirrors the read pattern in
 * pptx-tools.ts (get_selected_slide).
 *
 * CRITICAL: the read goes through the SAME serialized execution chain as the
 * tool handlers. PowerPoint.js cannot service concurrent PowerPoint.run()
 * contexts (see @usable/office-embed tool-execution) — an unserialized context
 * refresh racing a tool call (e.g. the user changes selection right before the
 * AI reads the slide) wedges both. Found live: get_selected_slide timing out
 * whenever the context refresh collided with it.
 */
import { HostContextProvider, capText, serializeExecution } from "@usable/office-embed";

const MAX_CONTEXT_CHARS = 4000;

async function read(): Promise<string | null> {
  try {
    return await serializeExecution(() => readSelectedSlideSnapshot(), {
      timeoutMs: 10_000,
      label: "context refresh",
    });
  } catch {
    // Timeout or Office error — skip this refresh; never break the embed.
    return null;
  }
}

async function readSelectedSlideSnapshot(): Promise<string | null> {
  try {
    return await PowerPoint.run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load("items/id");
      await context.sync();

      if (selected.items.length === 0) {
        return "The user is working in Microsoft PowerPoint. No slide is currently selected.";
      }

      const firstSlide = selected.items[0];

      // Resolve the selected slide's 0-based INDEX — the write tools are all
      // index-addressed, so without this the model knows what's ON the
      // selected slide but not WHICH slide it is, and edits the wrong one.
      const allSlides = context.presentation.slides;
      allSlides.load("items/id");
      const shapes = firstSlide.shapes;
      shapes.load("items/name,items/type");
      await context.sync();

      const selectedIndex = allSlides.items.findIndex((s) => s.id === firstSlide.id);

      // getTextFrameOrNullObject so shapes without a text frame don't throw.
      const textFrames = shapes.items.map((shape) => shape.getTextFrameOrNullObject());
      textFrames.forEach((tf) => tf.load("isNullObject,textRange/text"));
      await context.sync();

      const shapeTexts = shapes.items.map((shape, i) => {
        const tf = textFrames[i];
        return {
          name: shape.name,
          type: shape.type,
          text: tf.isNullObject ? "" : tf.textRange?.text ?? "",
        };
      });

      const lines = [
        "The user is working in Microsoft PowerPoint.",
        `SELECTED SLIDE: index ${selectedIndex} (slide ${selectedIndex + 1} of ${allSlides.items.length}). ` +
          `When the user says "this slide" / "the current slide", use slideIndex ${selectedIndex} in tool calls.`,
        `Selected slide count: ${selected.items.length}`,
        `Selected slide — shapes and text (JSON): ${JSON.stringify(shapeTexts)}`,
      ];
      return capText(lines.join("\n"), MAX_CONTEXT_CHARS);
    });
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onChange);
  return () => {
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: onChange,
    });
  };
}

export const pptxContextProvider: HostContextProvider = { read, subscribe };
